// src/components/layout/Header.tsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, currentUser, profile, logout } = useAuth();
  
  const isActive = (path: string) => {
    return location.pathname === path ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600';
  };

  // Determine correct documents link based on user type
  const documentsLink = '/broker/documents';
  const dashboardLink = profile?.role === 'broker' ? '/broker/dashboard' : '/carrier/dashboard';

  return (
    <header className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center">
                <img src="/images/loaddrive-logo.png" alt="LoadDrive" className="h-8" />
              </Link>
            </div>
            <nav className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link 
                to="/" 
                className={`inline-flex items-center px-1 pt-1 border-b-2 
                  ${location.pathname === '/' ? 'border-blue-500' : 'border-transparent'} 
                  ${isActive('/')}`}
              >
                Home
              </Link>
              <Link 
                to="/features" 
                className={`inline-flex items-center px-1 pt-1 border-b-2 
                  ${location.pathname === '/features' ? 'border-blue-500' : 'border-transparent'} 
                  ${isActive('/features')}`}
              >
                Features
              </Link>
              <Link 
                to="/pricing" 
                className={`inline-flex items-center px-1 pt-1 border-b-2 
                  ${location.pathname === '/pricing' ? 'border-blue-500' : 'border-transparent'} 
                  ${isActive('/pricing')}`}
              >
                Pricing
              </Link>
              <Link 
                to="/about" 
                className={`inline-flex items-center px-1 pt-1 border-b-2 
                  ${location.pathname === '/about' ? 'border-blue-500' : 'border-transparent'} 
                  ${isActive('/about')}`}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className={`inline-flex items-center px-1 pt-1 border-b-2 
                  ${location.pathname === '/contact' ? 'border-blue-500' : 'border-transparent'} 
                  ${isActive('/contact')}`}
              >
                Contact
              </Link>
            </nav>
          </div>
          
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {isAuthenticated ? (
              <div className="relative ml-3">
                {/* Dashboard Link */}
                <Link 
                  to={dashboardLink}
                  className={`inline-flex items-center px-3 py-2 mr-2 text-sm font-medium rounded-md
                    ${location.pathname === dashboardLink ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  Dashboard
                </Link>
                
                {/* Profile dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex text-sm bg-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full flex items-center justify-center bg-blue-600 text-white uppercase">
                      {currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  </button>
                  
                  {/* Dropdown menu */}
                  {profileMenuOpen && (
                    <div 
                      className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-10"
                    >
                      <div className="px-4 py-2 text-xs text-gray-500">
                        Signed in as <span className="font-medium">{currentUser?.email}</span>
                      </div>
                      {profile?.plan === 'Free' && (
                        <Link
                          to="/dashboard/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          Your Profile
                        </Link>
                      )}
                      <Link
                        to={documentsLink}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileMenuOpen(false)}
                      >
                        Documents
                      </Link>
                      <div className="border-t border-gray-100"></div>
                      <button
                        onClick={() => {
                          logout();
                          setProfileMenuOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  Log in
                </Link>
                <Link 
                  to="/register/carrier" 
                  className="ml-4 px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                 Carrier Signup
                </Link>
                <Link 
                  to="/register/broker" 
                  className="ml-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Broker Signup
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <span className="sr-only">Open main menu</span>
              {/* Icon when menu is closed */}
              <svg
                className={`${mobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {/* Icon when menu is open */}
              <svg
                className={`${mobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on state */}
      <div className={`${mobileMenuOpen ? 'block' : 'hidden'} sm:hidden`}>
        <div className="pt-2 pb-3 space-y-1">
          <Link
            to="/"
            className={`block pl-3 pr-4 py-2 border-l-4 ${
              location.pathname === '/' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/features"
            className={`block pl-3 pr-4 py-2 border-l-4 ${
              location.pathname === '/features' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Features
          </Link>
          <Link
            to="/pricing"
            className={`block pl-3 pr-4 py-2 border-l-4 ${
              location.pathname === '/pricing' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Pricing
          </Link>
          <Link
            to="/about"
            className={`block pl-3 pr-4 py-2 border-l-4 ${
              location.pathname === '/about' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            About
          </Link>
          <Link
            to="/contact"
            className={`block pl-3 pr-4 py-2 border-l-4 ${
              location.pathname === '/contact' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Contact
          </Link>
          
          {isAuthenticated ? (
            <>
              <Link
                to={dashboardLink}
                className={`block pl-3 pr-4 py-2 border-l-4 ${
                  location.pathname === dashboardLink ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
              {profile?.plan === 'Free' && (
                <Link
                  to="/dashboard/profile"
                  className={`block pl-3 pr-4 py-2 border-l-4 ${
                    location.pathname === '/dashboard/profile' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Profile
                </Link>
              )}
              <Link
                to={documentsLink}
                className={`block pl-3 pr-4 py-2 border-l-4 ${
                  location.pathname === documentsLink ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Documents
              </Link>
              <button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-red-600"
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-gray-600 hover:text-blue-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Log in
              </Link>
              <Link
                to="/register/carrier"
                className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-gray-700 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Carrier Signup
              </Link>
              <Link
                to="/register/broker"
                className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-blue-600 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Broker Signup
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
